import React from 'react';
import {
  Card,
} from 'react-bootstrap';
import Button from 'components/DesignSystem/Button';
import useAreaPredictionImage from 'hooks/Meteorology/Weather/Area/Status/useAreaPredictionImage';
import EnlargeableImage from 'components/Meteorology/Fields/SynopticCharts/SynopticChart/Map/EnlargeableImage';
import usePredictionImageDownload from 'hooks/Meteorology/Weather/Area/Status/usePredictionImageDownload';
import CentralizedSpinner from 'components/CentralizedSpinner';
import useModelOptions from 'hooks/Meteorology/Weather/Area/Status/useModelOptions';
import usePeriodOptions from 'hooks/Meteorology/Weather/Area/Status/usePeriodOptions';
import usePredictionParametersSelection from 'hooks/Meteorology/Weather/Area/Status/usePredictionParametersSelection';
import usePluma from 'hooks/Meteorology/Weather/Area/Status/usePluma';
import { useClosure } from 'utils/hooks';
import Selector from 'components/DesignSystem/Selector';
import useIsMobile from 'utils/hooks/useIsMobile';
import StatusCard from 'components/Meteorology/Weather/Area/Status/Mobile/StatusCard';
import IndexPredictionModal from './IndexPredictionModal';

export default function PreviewPanel({ area }: { area: string }) {
  const isMobile = useIsMobile();
  const [showIndexPrediction, setShowIndexPrediction] = React.useState(false);
  const onIndexPredictionClick = useClosure(setShowIndexPrediction, true);
  const onIndexPredictionHide = useClosure(setShowIndexPrediction, false);
  const {
    modelOptions,
    modelOptionsLoading,
  } = useModelOptions();
  const {
    periodOptions,
    periodOptionsLoading,
    triggerPeriods,
    setPeriodOptions,
  } = usePeriodOptions();
  const {
    predictionImageBase64,
    predictionImageLoading,
    setImage,
    triggerPrediction,
  } = useAreaPredictionImage();
  const {
    model,
    period,
    onModelChange,
    onPeriodChange,
  } = usePredictionParametersSelection({
    area,
    setPeriodOptions,
    setImage,
    triggerPeriods,
    triggerPrediction,
  });
  const { onPredictionDownloadClick, downloadDisabled } = usePredictionImageDownload(
    predictionImageBase64,
    predictionImageLoading,
    area,
    model,
    period,
  );
  const {
    data: indexPredictionData,
    isLoading: indexPredictionLoading,
    error: plumaError,
  } = usePluma(area);

  const loading = modelOptionsLoading || periodOptionsLoading;

  const modelSelector = (
    <Selector
      id="modelSelect"
      placeholder="Modelo"
      value={model}
      setValue={onModelChange}
      containerClassName="flex-grow-1 me-2"
    >
      {modelOptions.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </Selector>
  );

  const periodSelector = (
    <Selector
      id="predictionPeriodSelect"
      placeholder="Período"
      value={period}
      setValue={onPeriodChange}
      containerClassName="flex-grow-1 me-2"
    >
      {periodOptions.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </Selector>
  );

  const downloadButton = (
    <Button
      id="previewImageDownloadButton"
      small
      className={`icon-button weather-download-button${isMobile ? '' : ' ms-lg-3'}`}
      onClick={onPredictionDownloadClick}
      disabled={downloadDisabled}
      icon="download"
    />
  );

  const listButton = (
    <Button
      id="indexPredictionButton"
      small
      className={`icon-button weather-download-button${isMobile ? '' : ' ms-lg-3'}`}
      onClick={onIndexPredictionClick}
      disabled={indexPredictionLoading || !!plumaError}
      icon="list"
    />
  );

  const image = !predictionImageLoading && predictionImageBase64 && (
    <EnlargeableImage src={`data:image/png;base64,${predictionImageBase64}`} className="mt-3" />
  );

  const modal = (
    <IndexPredictionModal
      area={area}
      show={showIndexPrediction}
      onHide={onIndexPredictionHide}
      indexPredictionData={indexPredictionData}
    />
  );

  if (isMobile) {
    return (
      <>
        {modal}
        <StatusCard title="PREVISÃO ESPACIAL">
          {loading ? <CentralizedSpinner /> : (
            <>
              <div className="status-controls-mobile">
                {modelSelector}
                {periodSelector}
                {downloadButton}
                {listButton}
              </div>
              {predictionImageLoading && <CentralizedSpinner />}
              {image}
            </>
          )}
        </StatusCard>
      </>
    );
  }

  return (
    <>
      {modal}
      <Card className="shadow-sm forecast-instruction-card h-100">
        <Card.Header>
          PREVISÃO ESPACIAL
        </Card.Header>
        <Card.Body>
          {loading ? (
            <CentralizedSpinner />
          ) : (
            <>
              <div className="d-lg-flex mt-3 mx-3 align-items-end text-start">
                {modelSelector}
                {periodSelector}
                {downloadButton}
                {listButton}
              </div>
              {predictionImageLoading && <CentralizedSpinner />}
              {image}
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
